import {
  AMENDMENT_REASONS,
  AMENDMENT_STATUSES,
  APPROVAL_STEPS,
  CONTACT_ROLES,
  CONTRACT_KINDS,
  CONTRACT_STATUSES,
  DOC_RULE_CONDITIONS,
  DOC_RULE_CONTRACT_KINDS,
  FIN_STATUSES,
  FUNDING_SOURCES,
  type HistoryEntry,
  JOURNAL_TABLES,
  OBJECT_STATES,
  PAYMENT_BASES,
  PAYMENT_KINDS,
  PAYMENT_PRIORITIES,
  PAYMENT_REQUEST_STATUSES,
  PROCUREMENT_STAGES,
  PROJECT_STAGES,
  VAT_STATUSES,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { History as HistoryIcon } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import { useCan } from '../lib/session';
import { IconButton } from '../ui/button';
import { Sheet } from '../ui/dialog';
import { EmptyState, ErrorBox, Spinner } from '../ui/feedback';

export const TABLE_TITLES: Record<string, string> = {
  legal_entities: 'Юрлица',
  roles: 'Роли',
  memberships: 'Сотрудники',
  role_assignments: 'Назначения ролей',
  invites: 'Приглашения',
  delegations: 'Замещение',
  users: 'Учётные записи',
  tenants: 'Компания',
  files: 'Файлы',
  attachments: 'Вложения',
  tickets: 'Обращение в поддержку',
  suggestions: 'Предложение',
  entity_links: 'Связи',
  projects: 'Проект',
  directions: 'Направления',
  project_objects: 'Объекты проекта',
  precontract: 'Подготовку проекта до договора',
  card_rows: 'Карточку проекта',
  financing: 'Финансирование',
  budget_lines: 'Бюджет',
  budget_versions: 'Версии бюджета',
  milestones: 'Вехи',
  plans: 'Финансовый план',
  plan_rows: 'Строки финансового плана',
  cost_articles: 'Статьи расходов',
  requests: 'Заявку на закуп',
  request_lines: 'Позиции заявки на закуп',
  offers: 'КП',
  offer_lines: 'Цены КП',
  registries: 'Реестр',
  payment_requests: 'Заявку на оплату',
  payment_facts: 'Оплаты',
  payment_doc_rules: 'Комплекты документов к оплате',
  payment_moves: 'Переносы платежей',
  tenant_settings: 'Настройки компании',
  contracts: 'Договор',
  items: 'Спецификацию договора',
  schedule: 'Графики договора',
  approvals: 'Согласование договора',
  amendments: 'Допсоглашение',
  amendment_approvals: 'Согласование допсоглашения',
  contract_versions: 'Версии договора',
  counterparties: 'Контрагента',
  counterparty_contacts: 'Контакты контрагента',
  counterparty_docs: 'Документы контрагента',
  counterparty_stoplist: 'Стоп-лист',
  attention_items: 'Находки «Требует внимания»',
  tasks: 'Поручение',
  // График и ресурсы (поток 3)
  schedules: 'График проекта',
  activities: 'Работу графика',
  relations: 'Связь работ',
  wbs: 'Узел ИСР',
  calendars: 'Календарь',
  baselines: 'Базовый план',
  // Склад (поток 5)
  warehouses: 'Склады',
  catalog_items: 'Каталог ТМЦ',
  stock_documents: 'Документ склада',
  stock_document_lines: 'Строки документа склада',
  stock_reservations: 'Резервы склада',
  inventories: 'Инвентаризацию',
  inventory_lines: 'Строки ведомости',
  tool_units: 'Инструмент',
  tool_events: 'Историю инструмента',
};

export const FIELD_TITLES: Record<string, string> = {
  name: 'Наименование',
  short_name: 'Краткое название',
  legal_form: 'Форма',
  bin: 'БИН',
  address: 'Адрес',
  director_name: 'Руководитель',
  is_active: 'Активно',
  sort_order: 'Порядок',
  code: 'Код',
  description: 'Описание',
  permissions: 'Права',
  kind: 'Тип',
  is_system: 'Системная',
  status: 'Статус',
  position: 'Должность',
  department: 'Подразделение',
  is_owner: 'Владелец',
  user_id: 'Пользователь',
  role_id: 'Роль',
  membership_id: 'Сотрудник',
  scope_type: 'Область',
  scope_id: 'Объект области',
  email: 'Почта',
  full_name: 'ФИО',
  phone: 'Телефон',
  locale: 'Язык',
  password_hash: 'Пароль',
  expires_at: 'Действует до',
  accepted_at: 'Принято',
  accepted_user_id: 'Принял',
  revoked_at: 'Отозвано',
  assignments: 'Роли в приглашении',
  invited_by: 'Пригласил',
  original_name: 'Файл',
  mime_type: 'Тип файла',
  size_bytes: 'Размер',
  money_level: 'Уровень денег',
  title: 'Название',
  subject: 'Предмет',
  number: 'Номер',
  stage: 'Этап',
  stage_changed_at: 'Этап с',
  amount: 'Сумма',
  note: 'Примечание',
  comment: 'Комментарий',
  reason: 'Причина',
  reason_kind: 'Вид причины',
  return_reason: 'Причина возврата',
  reject_reason: 'Причина отказа',
  cancel_reason: 'Причина отмены',
  step: 'Пункт маршрута',
  round: 'Раунд согласования',
  due_at: 'Срок',
  due_date: 'Срок',
  decided_at: 'Решение принято',
  decided_by: 'Решил',
  approver_id: 'Согласующий',
  approved_at: 'Утверждено',
  approved_by: 'Утвердил',
  project_id: 'Проект',
  parent_id: 'Входит в',
  legal_entity_id: 'Юрлицо',
  direction_id: 'Направление',
  counterparty_id: 'Контрагент',
  supplier_id: 'Поставщик',
  contract_id: 'Договор',
  request_id: 'Заявка на закуп',
  request_line_id: 'Позиция заявки',
  registry_id: 'Реестр',
  offer_id: 'КП',
  amendment_id: 'Допсоглашение',
  budget_line_id: 'Строка бюджета',
  start_date: 'Начало',
  end_date: 'Окончание',
  signed_date: 'Дата подписания',
  signed_by: 'Подписал',
  signed_bypass: 'Подписан в обход маршрута',
  payment_terms: 'Порядок оплаты',
  cost_article: 'Статья ФП',
  article: 'Статья',
  selection_reason: 'Почему выбраны контрагент и цена',
  no_three_offers_reason: 'Обоснование закупки без 3 КП',
  selected_offer_id: 'Выбранное КП',
  security_memo: 'Служебная записка учредителям',
  security_check: 'Проверка службы безопасности',
  vat_status: 'НДС',
  vat_rate: 'Ставка НДС',
  currency: 'Валюта',
  card_confirmed_at: 'Карточка подтверждена',
  card_confirmed_by: 'Подтвердил карточку',
  one_c_number: 'Номер в 1С',
  one_c_date: 'Дата в 1С',
  one_c_status: 'Статус в 1С',
  one_c_registered_at: 'Регистрация в 1С',
  old_amount: 'Сумма до',
  new_amount: 'Новая сумма',
  old_end_date: 'Окончание до',
  new_end_date: 'Новое окончание',
  old_payment_terms: 'Порядок оплаты до',
  new_payment_terms: 'Новый порядок оплаты',
  new_cost_article: 'Новая статья ФП',
  new_items: 'Новая спецификация',
  new_schedule: 'Новые графики',
  scope_note: 'Изменение объёмов',
  before: 'Условия до',
  submitted: 'Условия на согласовании',
  prev_submitted: 'Условия прошлого раунда',
  snapshot: 'Условия версии',
  quantity: 'Количество',
  unit: 'Единица',
  price: 'Цена',
  estimate_price: 'Сметная цена',
  planned_date: 'Плановая дата',
  basis: 'Основание платежа',
  days: 'Дней',
  percent: 'Процент',
  done: 'Выполнено',
  paid_amount: 'Оплачено',
  paid_on: 'Дата оплаты',
  sent_at: 'Передано',
  over_reason: 'Обоснование сверх договора',
  over_approved_at: 'Согласовано сверх договора',
  over_approved_by: 'Согласовал сверх договора',
  received_on: 'Получено',
  is_selected: 'Выбрано',
  documents_ok: 'Документы проверены',
  required_date: 'Нужно к',
  initiator_id: 'Инициатор',
  buyer_id: 'Снабженец',
  assignee_id: 'Исполнитель',
  done_at: 'Выполнено',
  done_by: 'Выполнил',
  severity: 'Важность',
  details: 'Подробности',
  resolved_at: 'Закрыто',
  resolved_by: 'Закрыл',
  priority: 'Приоритет',
  funding_source: 'Источник денег',
  invoice_number: 'Номер счёта',
  invoice_date: 'Дата счёта',
  purpose: 'Назначение платежа',
  schedule_row_id: 'Плановый платёж договора',
  hold_reason: 'Причина остановки',
  hold_kind: 'Остановка',
  hold_decision: 'Решение финансового менеджера',
  hold_decided_by: 'Снял остановку',
  hold_decided_at: 'Остановка снята',
  from_date: 'Была дата',
  to_date: 'Новая дата',
  advance_guarantee: 'Гарантия возврата аванса',
  contract_kind: 'Вид договора',
  payment_kind: 'Тип платежа',
  condition: 'Условие',
  active: 'Действует',
  key: 'Документ',
  value: 'Значение',
  year: 'Год',
  months: 'Месяцы',
  values: 'Суммы по месяцам',
  section: 'Раздел',
  prepared_by: 'Подготовил',
  pto_by: 'ПТО',
  fm_by: 'Финансовый менеджер',
  curator_by: 'Куратор',
  // Работы графика (поток 3)
  duration_min: 'Длительность, мин',
  constraint_type: 'Ограничение',
  constraint_date: 'Дата ограничения',
  actual_start: 'Факт начала',
  actual_finish: 'Факт окончания',
  remaining_min: 'Осталось, мин',
};

/** Значения статусов и видов — словами, а не кодами */
const VALUE_TITLES: Record<string, Record<string, string>> = {
  'contracts.status': CONTRACT_STATUSES,
  'contracts.kind': CONTRACT_KINDS,
  'contracts.vat_status': VAT_STATUSES,
  'amendments.status': AMENDMENT_STATUSES,
  'amendments.reason_kind': AMENDMENT_REASONS,
  'requests.stage': PROCUREMENT_STAGES,
  'payment_requests.status': PAYMENT_REQUEST_STATUSES,
  'payment_requests.kind': PAYMENT_KINDS,
  'payment_requests.priority': PAYMENT_PRIORITIES,
  'payment_requests.funding_source': FUNDING_SOURCES,
  'payment_requests.hold_kind': {
    duplicate: 'возможная повторная оплата',
    limit: 'сверх лимита платежей',
    'duplicate,limit': 'возможная повторная оплата и сверх лимита',
  },
  'payment_doc_rules.contract_kind': DOC_RULE_CONTRACT_KINDS,
  'payment_doc_rules.payment_kind': PAYMENT_KINDS,
  'payment_doc_rules.condition': DOC_RULE_CONDITIONS,
  'schedule.basis': PAYMENT_BASES,
  'schedule.kind': { payment: 'платёж', delivery: 'поставка' },
  'plans.status': FIN_STATUSES,
  'projects.stage': PROJECT_STAGES,
  // Журнал изменений (ДС-11): представители контрагентов и объекты проекта
  'counterparty_contacts.role': CONTACT_ROLES,
  'project_objects.state': OBJECT_STATES,
};
const DECISIONS: Record<string, string> = {
  pending: 'ждёт решения',
  approved: 'согласовано',
  rejected: 'отклонено',
  returned: 'на доработку',
};
const STEPS: Record<string, string> = Object.fromEntries(
  Object.entries(APPROVAL_STEPS).map(([k, v]) => [k, v.title]),
);

export function valueTitle(table: string, field: string, v: unknown): unknown {
  if (typeof v !== 'string') return v;
  if ((table === 'approvals' || table === 'amendment_approvals') && field === 'status')
    return DECISIONS[v] ?? v;
  if (field === 'step') return STEPS[v] ?? v;
  return VALUE_TITLES[`${table}.${field}`]?.[v] ?? v;
}

/** «договор», «КП»: строчная первая буква, если это не сокращение */
function lowerFirst(s: string): string {
  return /^[A-ZА-ЯЁ][a-zа-яё]/.test(s) ? s[0]!.toLowerCase() + s.slice(1) : s;
}

const OP_TITLES = { I: 'создал', U: 'изменил', D: 'удалил' } as const;

/** Составные значения — словами: что это за данные, а не служебная запись */
const OBJECT_TITLES: Record<string, string> = {
  before: 'условия договора',
  submitted: 'условия договора',
  prev_submitted: 'условия договора',
  snapshot: 'условия договора',
  docs: 'отметки документов',
  doc_set: 'состав комплекта',
  values: 'суммы по месяцам',
  score_weights: 'веса критериев',
  settings: 'настройки',
  value: 'настройка',
  meta: 'данные записи',
};

export function show(v: unknown, field = ''): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'да' : 'нет';
  if (Array.isArray(v)) {
    if (!v.length) return '—';
    // Строки спецификации, графиков, сумм: число записей, а не служебный текст
    return v.every((x) => typeof x !== 'object' || x === null) ? v.join(', ') : `${v.length} строк`;
  }
  if (typeof v === 'object') return OBJECT_TITLES[field] ?? 'данные';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(s)) return dateTime(s);
  return s;
}

export function HistoryList({ entries }: { entries: HistoryEntry[] }) {
  if (!entries.length)
    return <EmptyState title="Изменений пока нет">Каждое сохранение будет записано здесь.</EmptyState>;
  return (
    <ol className="flex flex-col">
      {entries.map((e) => (
        <li key={e.id} className="relative border-l border-line pb-5 pl-5 last:pb-0">
          <span className="absolute top-1.5 -left-[4px] size-2 rounded-full bg-ink-3" aria-hidden />
          <div className="text-[13px] text-ink-2">
            <span className="num">{dateTime(e.at)}</span>
          </div>
          <div className="mt-0.5 text-[14px] text-ink">
            <b className="font-semibold">{e.actor?.fullName ?? 'Система'}</b> {OP_TITLES[e.op]}{' '}
            <span className="text-ink-2">{lowerFirst(TABLE_TITLES[e.table] ?? e.table)}</span>
          </div>
          {e.changes.length ? (
            <dl className="mt-2 flex flex-col gap-1.5 rounded-md bg-surface-2 px-3 py-2.5 text-[13px]">
              {e.changes.map((c) => (
                <div key={c.field} className="grid grid-cols-[minmax(110px,30%)_1fr] gap-2">
                  <dt className="text-ink-2">{FIELD_TITLES[c.field] ?? c.field}</dt>
                  <dd className="min-w-0 break-words text-ink">
                    {e.op === 'U' ? (
                      <>
                        <span className="text-ink-2 line-through decoration-ink-3">
                          {show(valueTitle(e.table, c.field, c.before), c.field)}
                        </span>
                        <span className="px-1.5 text-ink-3" aria-hidden>
                          →
                        </span>
                        <span>{show(valueTitle(e.table, c.field, c.after), c.field)}</span>
                      </>
                    ) : (
                      show(valueTitle(e.table, c.field, e.op === 'I' ? c.after : c.before), c.field)
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/**
 * Запись в журнале изменений: «схема.таблица:номер». Таблица карточки указана без схемы — схема находится по
 * перечню журнала; если таблиц с таким именем несколько, ссылки нет
 */
function journalRecord(table: string, rowId: string): string | null {
  const keys = Object.keys(JOURNAL_TABLES).filter((k) => k.split('.')[1] === table);
  return keys.length === 1 ? `${keys[0]}:${rowId}` : null;
}

/** Кнопка «История» у любой записи: открывает панель «кто, что, когда, было → стало». */
export function HistoryButton({ table, rowId, title }: { table: string; rowId: string; title?: string }) {
  const [open, setOpen] = useState(false);
  const canJournal = useCan('admin.audit');
  const record = canJournal ? journalRecord(table, rowId) : null;
  const q = useQuery({
    queryKey: ['history', table, rowId],
    queryFn: () => api<HistoryEntry[]>('/history', { query: { table, rowId } }),
    enabled: open,
  });
  return (
    <>
      <IconButton label="История изменений" onClick={() => setOpen(true)}>
        <HistoryIcon className="size-4" />
      </IconButton>
      <Sheet open={open} onOpenChange={setOpen} title="История изменений" description={title}>
        {q.isLoading ? (
          <Spinner />
        ) : q.error ? (
          <ErrorBox error={q.error} />
        ) : (
          <HistoryList entries={q.data ?? []} />
        )}
        {record ? (
          <div className="mt-5 border-t border-line pt-3">
            {/* Журнал изменений (ДС-11): состояние записи на любой момент и возврат с проверкой */}
            <Link
              to="/admin/audit"
              search={{ tab: 'changes', record }}
              className="inline-flex min-h-11 items-center text-[14px] font-medium text-ink underline underline-offset-2 md:min-h-0"
            >
              Журнал изменений: состояние на дату и возврат
            </Link>
          </div>
        ) : null}
      </Sheet>
    </>
  );
}
